/**
 * The session's meeting, on the Documents tab (Brief K slice 2a, K7; mig 871).
 *
 * Link a Zoom, Google Meet or Teams meeting here and, when it ends with a
 * transcript, the transcript arrives as one of the session's documents on its
 * own. Below it, "meetings we couldn't place": finished meetings no session
 * holds, which a person attaches here or sets aside.
 *
 * Drawn only for people who may add documents (the panel's canAdd); the
 * database functions make the same check.
 */
import { useCallback, useEffect, useState } from 'react';
import { Video } from 'lucide-react';
import {
  MEETING_PROVIDER_LABELS,
  attachWaitingMeeting,
  getSessionMeeting,
  listWaitingMeetings,
  setSessionMeeting,
  setWaitingMeetingAside,
  type SessionMeeting,
  type WaitingMeeting,
} from '../../../lib/sessionMeetingService';

function when(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function SessionMeetingSection({
  sessionId,
  workspaceId,
  onDocumentArrived,
}: {
  sessionId: string;
  workspaceId: string;
  /** Called after a meeting is attached here, so the document list reloads. */
  onDocumentArrived: () => void;
}) {
  const [meeting, setMeeting] = useState<SessionMeeting | null>(null);
  const [waiting, setWaiting] = useState<WaitingMeeting[]>([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [m, w] = await Promise.all([getSessionMeeting(sessionId), listWaitingMeetings(workspaceId)]);
    setMeeting(m);
    setWaiting(w);
  }, [sessionId, workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2.5 space-y-2" data-testid="session-meeting">
      <div className="flex items-start gap-2">
        <Video className="h-4 w-4 mt-0.5 text-[var(--text-ter)] shrink-0" />
        <div className="min-w-0 flex-1 text-sm">
          {editing ? (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void act(async () => {
                  await setSessionMeeting(sessionId, draft);
                  setEditing(false);
                });
              }}
            >
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Paste the Zoom, Google Meet or Teams link"
                aria-label="Meeting link"
                className="min-w-0 flex-1 border border-[var(--rule)] bg-[var(--surface)] px-2 py-1 text-sm"
              />
              <button type="submit" disabled={busy || !draft.trim()} className="px-2.5 py-1 text-xs bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                Link meeting
              </button>
              <button type="button" onClick={() => setEditing(false)} className="px-2 py-1 text-xs text-[var(--text-sec)]">
                Cancel
              </button>
            </form>
          ) : meeting ? (
            <p className="text-[var(--text-pri)]">
              Held in a {MEETING_PROVIDER_LABELS[meeting.provider]} meeting. When it ends with a transcript, the transcript
              arrives here on its own.{' '}
              <button type="button" onClick={() => { setDraft(meeting.meetingUrl ?? meeting.meetingRef); setEditing(true); }} className="text-xs text-primary-600 hover:underline">
                Change
              </button>{' '}
              <button type="button" disabled={busy} onClick={() => void act(() => setSessionMeeting(sessionId, null))} className="text-xs text-[var(--text-sec)] hover:underline">
                Unlink
              </button>
            </p>
          ) : (
            <p className="text-[var(--text-sec)]">
              Meeting on Zoom, Google Meet or Teams? Link it and its transcript arrives here when the meeting ends.{' '}
              <button type="button" onClick={() => { setDraft(''); setEditing(true); }} className="text-xs text-primary-600 hover:underline">
                Link a meeting
              </button>
            </p>
          )}
          {error && <p className="mt-1 text-xs text-[var(--coral)]">{error}</p>}
        </div>
      </div>

      {waiting.length > 0 && (
        <div className="border-t border-[var(--rule)] pt-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">Meetings we couldn&rsquo;t place</p>
          <ul className="space-y-1">
            {waiting.map((w) => (
              <li key={w.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="min-w-0 flex-1 text-[var(--text-pri)]">
                  {w.topic ?? `${MEETING_PROVIDER_LABELS[w.provider]} meeting`}
                  <span className="text-xs text-[var(--text-ter)]"> · {MEETING_PROVIDER_LABELS[w.provider]}{w.startedAt ? ` · ${when(w.startedAt)}` : ''}</span>
                </span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void act(async () => { await attachWaitingMeeting(w.id, sessionId); onDocumentArrived(); })}
                  className="text-xs text-primary-600 hover:underline disabled:opacity-50"
                >
                  Attach to this session
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void act(() => setWaitingMeetingAside(w.id))}
                  className="text-xs text-[var(--text-sec)] hover:underline disabled:opacity-50"
                >
                  Set aside
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
